import { ButtonColor } from "@/common/enum/button-color";
import { Permission } from "@/common/enum/permission";
import { Button, Modal } from "flowbite-react";
import React from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { toastState } from "../../atoms/system/toast-state";
import { adminPageState } from "../../atoms/user/admin-page-state";
import { UserStatus } from "../../common/enum/user-status";
import { userService } from "../../services/user-service";
import { UserDto } from "../../types/dto/user-dto";
import { UpdateAdminPayload } from "../../types/payload/update-admin-payload";
import { GrayExclamationCircleIcon } from "../icon/GrayExclamationCircleIcon";
import { Body16Regular } from "../typography/Body16Regular";

interface Props {
  user: UserDto;
  closeModal: () => void;
}

export const PlatformAdminStatusModal: React.FC<Props> = ({
  user,
  closeModal,
}) => {
  const [page, setPage] = useRecoilState(adminPageState);
  const setToast = useSetRecoilState(toastState);

  const isActive = user.status === UserStatus.ACTIVE;
  const currentPermission = user.permission;

  const getPermissionText = (permission: Permission | null) => {
    switch (permission) {
      case Permission.READ:
        return "읽기";
      case Permission.WRITE:
        return "쓰기";
      default:
        return "미지정";
    }
  };

  const getPayload = (action: string): UpdateAdminPayload => {
    const payloadMap: Record<string, UpdateAdminPayload> = {
      activate: { status: UserStatus.ACTIVE },
      deactivate: { status: UserStatus.INACTIVE },
      write: { permission: Permission.WRITE } as any,
      read: { permission: Permission.READ } as any,
    };
    return payloadMap[action];
  };

  const handleAction = async (action: string) => {
    const payload = !isActive ? getPayload("activate") : getPayload(action);

    try {
      const updateResponse = await userService.updateAdminAsync(
        user.id,
        payload
      );
      if (!updateResponse.ok) return;

      const getUserResponse = await userService.getAdminById(user.id);
      if (!getUserResponse.ok) return;

      setPage((prev) => ({
        ...prev,
        contents: prev.contents.map((item) =>
          item.id === user.id ? getUserResponse.data : item
        ),
      }));

      setToast({
        show: true,
        messages: ["전환되었습니다."],
      });
    } catch (error) {
      console.log(error);
    }
    closeModal();
  };

  const getPermissionButtons = () => {
    const buttons = [];

    if (currentPermission === Permission.READ) {
      buttons.push(
        <Button
          key="write"
          color={ButtonColor.PRIMARY}
          onClick={() => handleAction("write")}
        >
          쓰기 권한 부여
        </Button>
      );
    } else if (currentPermission === Permission.WRITE) {
      buttons.push(
        <Button
          key="read"
          color={ButtonColor.PRIMARY}
          onClick={() => handleAction("read")}
        >
          읽기 권한 부여
        </Button>
      );
    } else {
      buttons.push(
        <Button
          key="write"
          color={ButtonColor.PRIMARY}
          onClick={() => handleAction("write")}
        >
          쓰기 권한 부여
        </Button>,
        <Button
          key="read"
          color={ButtonColor.PRIMARY}
          onClick={() => handleAction("read")}
        >
          읽기 권한 부여
        </Button>
      );
    }

    return buttons;
  };

  return (
    <Modal size="md" show={true} onClose={closeModal}>
      <Modal.Header className="pt-[10px] px-[10px] h-[36px] border-none"></Modal.Header>
      <Modal.Body className="p-0">
        <div className="flex flex-col items-center box-border py-[20px] gap-[16px]">
          <div className="w-[42px] h-[42px] flex items-center justify-center">
            <GrayExclamationCircleIcon size={35} />
          </div>

          <div className="flex flex-col items-center text-center">
            {isActive ? (
              <>
                <Body16Regular className="text-semantic-text-secondary">
                  {user.name}님의 권한을 변경하시겠어요?
                </Body16Regular>
                <Body16Regular className="text-semantic-text-secondary">
                  현재 권한: {getPermissionText(currentPermission)}
                </Body16Regular>
              </>
            ) : (
              <>
                <Body16Regular className="text-semantic-text-secondary">
                  {user.name}님 계정을 활성화 하시겠어요?
                </Body16Regular>
                <Body16Regular className="text-semantic-text-secondary">
                  로그인 및 전체 관리자 페이지에 접근할 수 있게 됩니다.
                </Body16Regular>
              </>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="pt-0 border-none">
        <div className="flex gap-[8px] items-center justify-center w-full">
          <Button color={ButtonColor.TRANSPARENT_OUTLINE} onClick={closeModal}>
            취소하기
          </Button>

          {isActive ? (
            <>
              {getPermissionButtons()}
              <Button
                color={ButtonColor.RED}
                onClick={() => handleAction("deactivate")}
              >
                비활성화
              </Button>
            </>
          ) : (
            <Button
              color={ButtonColor.PRIMARY}
              onClick={() => handleAction("activate")}
            >
              활성화
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};
